import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  useTheme,
  Tab,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import StarIcon from "@mui/icons-material/Star";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { classifyInboxMediaFile, getInboxAcceptString } from "../../../utils/inboxMediaValidation";
import { getAuthToken } from "../../../utils/getAuthToken";
import axios from "axios";
import toast from "react-hot-toast";

const SERVER  = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const FILTERS = ["All", "Images", "Videos", "Documents", "Audio"];
const FILTER_TO_TYPE = { All: "all", Images: "image", Videos: "video", Documents: "document", Audio: "audio" };
const FILTER_ICONS = {
  Images: ImageOutlinedIcon,
  Videos: PlayCircleOutlineIcon,
  Documents: DescriptionOutlinedIcon,
  Audio: MusicNoteOutlinedIcon,
};

const formatGalleryDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getFileExtLabel = (item) => {
  const name = (item?.originalName || item?.filename || "").toLowerCase();
  const extFromName = name.includes(".") ? name.split(".").pop() : null;
  const extFromMime = (item?.mimeType || "").split("/").pop();
  return (extFromName || extFromMime || "").toUpperCase();
};

const FileTypeIcon = ({ mediaType, sx }) => {
  if (mediaType === "image")  return <ImageIcon sx={sx} />;
  if (mediaType === "video")  return <VideoFileIcon sx={sx} />;
  if (mediaType === "audio")  return <AudioFileIcon sx={sx} />;
  return <InsertDriveFileIcon sx={sx} />;
};

/** Gallery grid image thumbnail — falls back to a themed icon (not the browser's
 * raw broken-image glyph) if the file can't be loaded, e.g. removed from disk. */
const GalleryImageThumb = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);

  if (errored || !src) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
        <ImageOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        <Typography sx={{ fontSize: "0.68rem", color: "text.disabled" }}>Preview unavailable</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", transition: ".2s" }}
    />
  );
};

const formatBytes = (bytes) => {
  if (bytes < 1024)            return `${bytes} B`;
  if (bytes < 1024 * 1024)    return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const sortGalleryItems = (items = []) => {
  return [...items].sort((a, b) => Number(b?.isStarred || false) - Number(a?.isStarred || false));
};

const getMediaPreview = (item) => {
  const name = (item?.originalName || item?.filename || "").toLowerCase();
  const mime = (item?.mimeType || "").toLowerCase();

  if (item?.mediaType === "image") return { kind: "image", icon: null };
  if (item?.mediaType === "video") return { kind: "video", icon: <VideoFileIcon sx={{ fontSize: 34, color: "text.secondary" }} /> };
  if (item?.mediaType === "audio") return { kind: "audio", icon: <AudioFileIcon sx={{ fontSize: 34, color: "text.secondary" }} /> };
  if (mime.includes("pdf") || name.endsWith(".pdf")) return { kind: "pdf", icon: <PictureAsPdfOutlinedIcon sx={{ fontSize: 34, color: "text.secondary" }} /> };
  if (mime.includes("sheet") || mime.includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) {
    return { kind: "spreadsheet", icon: <TableChartOutlinedIcon sx={{ fontSize: 34, color: "text.secondary" }} /> };
  }
  return { kind: "document", icon: <InsertDriveFileIcon sx={{ fontSize: 34, color: "text.secondary" }} /> };
};

const getFileTypeBadgeStyle = (item) => {
  const name = (item?.originalName || item?.filename || "").toLowerCase();
  const mime = (item?.mimeType || "").toLowerCase();
  if (item?.mediaType === "image") return { bg: "#3B82F6", Icon: ImageIcon };
  if (item?.mediaType === "video") return { bg: "#8B5CF6", Icon: VideoFileIcon };
  if (item?.mediaType === "audio") return { bg: "#F59E0B", Icon: AudioFileIcon };
  if (mime.includes("pdf") || name.endsWith(".pdf")) return { bg: "#EF4444", Icon: PictureAsPdfOutlinedIcon };
  if (mime.includes("sheet") || mime.includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) {
    return { bg: "#22C55E", Icon: TableChartOutlinedIcon };
  }
  return { bg: "#6B7280", Icon: InsertDriveFileIcon };
};

const ClusterDenMediaDialog = ({
  open,
  onClose,
  onFilesSelected,
  onGalleryItemSelected,
  platform = "whatsapp",
  isTeamMemberChat = false,
}) => {
  const theme = useTheme();

  const [tab, setTab] = useState(0);

  // ── Gallery state ──────────────────────────────────────────────────────────
  const [galleryItems, setGalleryItems]     = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError]     = useState(null);
  const [selectedItems, setSelectedItems]   = useState([]);
  const [inserting, setInserting]           = useState(false);
  const [search, setSearch]                 = useState("");
  const [filter, setFilter]                 = useState("All");
  const [hasMore, setHasMore]               = useState(false);
  const [page, setPage]                     = useState(1);
  const [editingItemId, setEditingItemId]   = useState(null);
  const [editingName, setEditingName]       = useState("");
  const searchTimerRef                      = useRef(null);

  // ── Upload state ───────────────────────────────────────────────────────────
  const [dragActive, setDragActive]       = useState(false);
  const [stagedFiles, setStagedFiles]     = useState([]);
  const [uploading, setUploading]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const uploadInputRef                    = useRef(null);
  const [previewStagedFile, setPreviewStagedFile] = useState(null);

  const accept = getInboxAcceptString(platform, isTeamMemberChat);

  // ── Gallery fetch ──────────────────────────────────────────────────────────

  const fetchGallery = useCallback(async (searchVal, filterVal, pageVal, append = false) => {
    setGalleryLoading(true);
    setGalleryError(null);
    try {
      const token      = await getAuthToken();
      const mediaType  = FILTER_TO_TYPE[filterVal] || "all";
      const { data }   = await axios.get(`${SERVER}/api/gallery/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { mediaType, search: searchVal || undefined, page: pageVal, limit: 30 },
      });
      if (data.success) {
        setGalleryItems((prev) => sortGalleryItems(append ? [...prev, ...data.data] : data.data));
        setHasMore(data.hasMore);
      }
    } catch {
      setGalleryError("Failed to load gallery");
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  const toggleItem = (item) => {
    setSelectedItems((prev) =>
      prev.some((i) => i._id === item._id)
        ? prev.filter((i) => i._id !== item._id)
        : [...prev, item]
    );
  };

  useEffect(() => {
    if (!open || tab !== 0) return;
    setPage(1);
    setSelectedItems([]);
    // Intentionally excludes `search`: typing already triggers a separate
    // debounced fetch via handleSearchChange. Reacting to `search` here too
    // would fire an extra, non-debounced request on every keystroke.
    fetchGallery(search, filter, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, filter, fetchGallery]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      setSelectedItems([]);
      fetchGallery(val, filter, 1, false);
    }, 350);
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchGallery(search, filter, next, true);
  };

  const startEditingItem = (e, item) => {
    e.stopPropagation();
    setEditingItemId(item._id);
    setEditingName(item.originalName || item.filename || "");
  };

  const handleRenameItem = async (e, item) => {
    e.stopPropagation();
    const nextName = editingName.trim();
    if (!item?._id || !nextName) {
      setEditingItemId(null);
      setEditingName("");
      return;
    }

    try {
      const token = await getAuthToken();
      const { data } = await axios.patch(`${SERVER}/api/gallery/${item._id}/rename`, { name: nextName }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.success) {
        const updatedName = data.data?.originalName || nextName;
        setGalleryItems((prev) => prev.map((entry) => entry._id === item._id ? { ...entry, originalName: updatedName } : entry));
        setSelectedItems((prev) => prev.map((i) => i._id === item._id ? { ...i, originalName: updatedName } : i));
        toast.success("File name updated");
      }
    } catch {
      toast.error("Failed to update file name");
    } finally {
      setEditingItemId(null);
      setEditingName("");
    }
  };

  // ── Gallery delete ─────────────────────────────────────────────────────────

  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const handleRequestDeleteItem = (e, item) => {
    e.stopPropagation();
    setDeleteConfirmItem(item);
  };

  const handleCancelDeleteItem = () => {
    if (deleteInProgress) return;
    setDeleteConfirmItem(null);
  };

  const handleConfirmDeleteItem = async () => {
    const item = deleteConfirmItem;
    if (!item) return;
    setDeleteInProgress(true);
    try {
      const token = await getAuthToken();
      await axios.delete(`${SERVER}/api/gallery/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGalleryItems((prev) => prev.filter((i) => i._id !== item._id));
      setSelectedItems((prev) => prev.filter((i) => i._id !== item._id));
      setDeleteConfirmItem(null);
    } catch {
      toast.error("Failed to delete item");
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleToggleStarItem = (e, item) => {
    e.stopPropagation();
    setGalleryItems((prev) => sortGalleryItems(prev.map((entry) => entry._id === item._id ? { ...entry, isStarred: !Boolean(entry.isStarred) } : entry)));
  };

  // ── Gallery insert ─────────────────────────────────────────────────────────

  const handleInsert = async () => {
    if (!selectedItems.length) return;
    setInserting(true);
    try {
      if (onGalleryItemSelected) {
        selectedItems.forEach((item) => onGalleryItemSelected(item));
        handleClose();
      } else {
        const files = await Promise.all(
          selectedItems.map(async (item) => {
            const response = await fetch(item.localUrl);
            if (!response.ok) throw new Error("fetch failed");
            const blob = await response.blob();
            return new File([blob], item.originalName, { type: item.mimeType });
          })
        );
        onFilesSelected(files);
        handleClose();
      }
    } catch {
      toast.error("Failed to send gallery items");
    } finally {
      setInserting(false);
    }
  };

  // ── Upload to gallery ──────────────────────────────────────────────────────

  const stageFiles = useCallback((files) => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length) return;
    const valid = [], errors = [];
    incoming.forEach((f) => {
      const v = classifyInboxMediaFile(f, platform, isTeamMemberChat);
      if (v.valid) valid.push(f);
      else errors.push(`${f.name}: ${v.error}`);
    });
    if (errors.length) toast.error(errors.slice(0, 2).join("\n"));
    if (valid.length)  setStagedFiles((prev) => [...prev, ...valid]);
  }, [platform, isTeamMemberChat]);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    stageFiles(e.dataTransfer.files);
  };

  /**
   * Upload all staged files to the gallery one by one.
   * On completion, switch to Gallery tab and refresh.
   */
  const handleUploadToGallery = async () => {
    if (!stagedFiles.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: stagedFiles.length });

    const token = await getAuthToken();
    let successCount = 0;

    for (const file of stagedFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${SERVER}/api/gallery/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        successCount++;
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        toast.error(`Failed to upload "${file.name}": ${msg}`);
      }
      setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
    }

    setUploading(false);
    setStagedFiles([]);

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} added to gallery`);
      // Switch to gallery tab and refresh so user can select the new files
      setSearch("");
      setFilter("All");
      setPage(1);
      setSelectedItems([]);
      setTab(0);
      fetchGallery("", "All", 1, false);
    }
  };

  // ── Reset & close ──────────────────────────────────────────────────────────

  const handleClose = () => {
    if (uploading) return; // block close while uploading
    setSearch(""); setFilter("All"); setSelectedItems([]);
    setStagedFiles([]); setDragActive(false); setTab(0);
    setGalleryItems([]); setPage(1); setHasMore(false); setGalleryError(null);
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const deleteConfirmName = deleteConfirmItem
    ? (deleteConfirmItem.originalName || deleteConfirmItem.filename || "this file")
    : "";

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: 800,
          display: "flex",
          flexDirection: "column",
          bgcolor: (t) => t.palette.background.paper,
          backgroundImage: "none",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0, pt: 3, px: 3.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(39,109,93,0.18)" : "rgba(39,109,93,0.09)",
            }}
          >
            <PhotoLibraryOutlinedIcon sx={{ fontSize: 20, color: "#276d5d" }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
            }}
          >
            Clusterden Gallery
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={uploading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3.5, mt: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { if (!uploading) { setTab(v); setSelectedItems([]); } }}
          TabIndicatorProps={{ style: { backgroundColor: "#276d5d", height: 2.5 } }}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minWidth: 0, px: 0, mr: 3.5, fontSize: "0.8125rem", minHeight: 40 },
            "& .Mui-selected": { color: "#276d5d !important" },
          }}
        >
          <Tab label="Your Gallery" />
          <Tab label="Upload" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

        {/* ── YOUR GALLERY TAB ── */}
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", py: 2.5, px: 3.5, overflow: "hidden" }}>
            <TextField
              placeholder="Search files..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 21, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#FAFBFC",
                },
                "& .MuiOutlinedInput-input": {
                  py: "12px",
                  fontSize: "0.92rem",
                },
                "& .MuiOutlinedInput-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.85,
                },
              }}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
              {FILTERS.map((f) => {
                const FilterIcon = FILTER_ICONS[f];
                const selected = filter === f;
                return (
                  <Chip
                    key={f}
                    label={f}
                    icon={FilterIcon ? <FilterIcon sx={{ fontSize: "17px !important", color: selected ? "#fff !important" : undefined }} /> : undefined}
                    onClick={() => setFilter(f)}
                    variant={selected ? "filled" : "outlined"}
                    sx={{
                      borderRadius: "8px",
                      height: 34,
                      px: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: selected ? 600 : 500,
                      backgroundColor: selected ? "#276d5d" : "transparent",
                      color: selected ? "#fff" : "text.secondary",
                      borderColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#E5E7EB",
                      "&:hover": { backgroundColor: selected ? "#1f5447" : (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F3F4F6" },
                    }}
                  />
                );
              })}
            </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                pr: 1.5,
                mr: -1.5,
                scrollbarWidth: "thin",
                scrollbarColor: (t) =>
                  t.palette.mode === "dark" ? "rgba(255,255,255,0.18) transparent" : "rgba(15,23,42,0.15) transparent",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 8,
                  backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.15)",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(15,23,42,0.25)",
                },
              }}
            >
              {galleryLoading && galleryItems.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <CircularProgress size={28} sx={{ color: theme.palette.primary.main }} />
                </Box>
              ) : galleryError ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.9rem" }} color="error">{galleryError}</Typography>
                  <Button size="small" onClick={() => fetchGallery(search, filter, 1, false)} sx={{ color: theme.palette.primary.main, textTransform: "none" }}>Retry</Button>
                </Box>
              ) : galleryItems.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1.5, color: "text.secondary" }}>
                  <FileTypeIcon mediaType={FILTER_TO_TYPE[filter]} sx={{ fontSize: 96, opacity: 0.3 }} />
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, opacity: 0.75 }}>
                    {search ? "No files match your search" : "No media in gallery yet"}
                  </Typography>
                  {!search && (
                    <Button
                      variant="contained"
                      onClick={() => setTab(1)}
                      sx={{
                        textTransform: "none",
                        mt: 1,
                        px: 3,
                        py: 1,
                        fontSize: "0.8125rem",
                        borderRadius: "10px",
                        backgroundColor: "#276d5d",
                        "&:hover": { backgroundColor: "#1f5447" },
                      }}
                    >
                      Upload files
                    </Button>
                  )}
                </Box>
              ) : (
                <>
                  <Box sx={{ display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                    gap: { xs: 1.35, md: 1.75 } }}>
                  {galleryItems.map((item) => {
  const isSelected = selectedItems.some((i) => i._id === item._id);
  const preview = getMediaPreview(item);
  const { bg: badgeBg, Icon: BadgeIcon } = getFileTypeBadgeStyle(item);
  const filename = item.originalName || item.filename || "Untitled file";
  const metaParts = [
    typeof item.size === "number" ? formatBytes(item.size) : null,
    getFileExtLabel(item) || null,
    formatGalleryDate(item.createdAt),
  ].filter(Boolean);

  const selectedIndex = selectedItems.findIndex((i) => i._id === item._id);

  return (
    <Box
      key={item._id}
      onClick={() => toggleItem(item)}
      sx={{
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: (t) => t.palette.mode === "dark" ? "#242424" : "#fff",
        border: isSelected
          ? `2px solid ${theme.palette.primary.main}`
          : (t) => `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#E5E7EB"}`,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "border-color .2s ease",
        "&:hover": {
          borderColor: isSelected ? undefined : (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.24)" : "#D1D5DB",
          "& .gdrive-hover-overlay": { opacity: 1 },
        },
      }}
    >
      {/* Google-Drive-style hover overlay: subtle darken, purely visual (no pointer events) */}
      <Box
        className="gdrive-hover-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
          opacity: 0,
          transition: "opacity .15s ease",
          backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.018)",
        }}
      />

      {/* Action Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.25,
          pt: 1,
          pb: 0.5,
        }}
      >
        {isSelected ? (
          <Box
            sx={{
              width: 19,
              height: 19,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
              {selectedIndex + 1}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: 17,
              height: 17,
              borderRadius: "5px",
              border: "1.5px solid",
              borderColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "#D1D5DB",
            }}
          />
        )}

        <Stack direction="row" spacing={0.25} onClick={(e) => e.stopPropagation()}>
          <IconButton
            size="small"
            onClick={(e) => handleRequestDeleteItem(e, item)}
            sx={{
              width: 32,
              height: 32,
              color: "text.secondary",
              "&:hover": { color: "error.main", bgcolor: (t) => t.palette.mode === "dark" ? "rgba(239,68,68,0.16)" : "#FEE2E2" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => startEditingItem(e, item)}
            sx={{
              width: 32,
              height: 32,
              color: "text.secondary",
              "&:hover": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#F3F4F6" },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleToggleStarItem(e, item)}
            sx={{
              width: 32,
              height: 32,
              color: item?.isStarred ? "#F59E0B" : "text.secondary",
              "&:hover": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(245,158,11,0.16)" : "#FEF3C7" },
            }}
          >
            {item?.isStarred ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderOutlinedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      </Box>

      {/* Preview — shorter than wide, so once the action bar + footer chrome is added on top,
          the OVERALL card (not just this photo strip) reads as roughly square. */}
      <Box
        sx={{
          aspectRatio: "1 / 0.68",
          bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFC",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          px: 2,
          py: 1,
        }}
      >
        {item.mediaType === "image" ? (
          <GalleryImageThumb src={item.localUrl} alt={filename} />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            {React.cloneElement(preview.icon, { sx: { fontSize: 64, color: "text.secondary" } })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          minHeight: 58,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 1,
          gap: 1,
        }}
      >
        {/* File type badge — bottom left */}
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            bgcolor: badgeBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BadgeIcon sx={{ fontSize: 16, color: "#fff" }} />
        </Box>

        {editingItemId === item._id ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={editingName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={(e) => handleRenameItem(e, item)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameItem(e, item);
              if (e.key === "Escape") { setEditingItemId(null); setEditingName(""); }
            }}
          />
        ) : (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
              {filename}
            </Typography>
            {metaParts.length > 0 && (
              <Typography noWrap sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                {metaParts.join(" · ")}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
})}
                  </Box>

                  {hasMore && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                      <Button
                        onClick={handleLoadMore}
                        disabled={galleryLoading}
                        sx={{
                          color: "#276d5d",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(39,109,93,0.14)" : "rgba(39,109,93,0.08)" },
                          "&.Mui-disabled": { color: "text.disabled" },
                        }}
                      >
                        {galleryLoading ? <CircularProgress size={16} sx={{ color: "#276d5d" }} /> : "Load more"}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}

        {/* ── UPLOAD TAB ── */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>

            {/* Upload progress bar */}
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mb: 0.5, display: "block" }}>
                  Uploading {uploadProgress.done} / {uploadProgress.total}…
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(uploadProgress.done / uploadProgress.total) * 100}
                  sx={{ borderRadius: 4, "& .MuiLinearProgress-bar": { backgroundColor: "#276d5d" } }}
                />
              </Box>
            )}

            {/* Drop zone */}
            <Box
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => !uploading && uploadInputRef.current?.click()}
              sx={{
                position: "relative",
                overflow: "hidden",
                border: (t) => `2px dashed ${dragActive ? "#276d5d" : t.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "#D8DCE1"}`,
                borderRadius: "14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                p: 3,
                cursor: uploading ? "not-allowed" : "pointer",
                opacity: uploading ? 0.5 : 1,
                transition: "border-color 0.2s, background-color 0.2s",
                backgroundColor: dragActive
                  ? "rgba(39,109,93,0.06)"
                  : (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.015)" : "#FAFBFC",
                "&:hover": !uploading ? { borderColor: "#276d5d", backgroundColor: "rgba(39,109,93,0.04)" } : {},
                flexShrink: 0,
                minHeight: 180,
              }}
            >
              {/* Decorative faint file-type icons, purely visual */}
              <ImageOutlinedIcon sx={{ position: "absolute", top: 38, left: "27%", fontSize: 22, color: "text.secondary", opacity: 0.18 }} />
              <DescriptionOutlinedIcon sx={{ position: "absolute", top: 34, right: "26%", fontSize: 20, color: "text.secondary", opacity: 0.18 }} />
              <MusicNoteOutlinedIcon sx={{ position: "absolute", bottom: 34, left: "24%", fontSize: 20, color: "text.secondary", opacity: 0.16 }} />
              <PlayCircleOutlineIcon sx={{ position: "absolute", bottom: 30, right: "24%", fontSize: 20, color: "text.secondary", opacity: 0.16 }} />
              <Box sx={{ position: "absolute", top: "42%", left: "18%", width: 6, height: 6, borderRadius: "50%", bgcolor: "text.secondary", opacity: 0.16 }} />
              <Box sx={{ position: "absolute", top: "40%", right: "20%", width: 5, height: 5, borderRadius: "50%", bgcolor: "text.secondary", opacity: 0.16 }} />

              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                  backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(39,109,93,0.18)" : "rgba(39,109,93,0.09)",
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 46, color: "#276d5d" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Drag &amp; drop files here</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", my: 0.5 }}>or</Typography>
              <Button
                variant="contained"
                disabled={uploading}
                startIcon={<FolderOpenIcon sx={{ fontSize: 20 }} />}
                onClick={(e) => { e.stopPropagation(); uploadInputRef.current?.click(); }}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  px: 3,
                  py: 1,
                  backgroundColor: "#276d5d",
                  "&:hover": { backgroundColor: "#1f5447" },
                }}
              >
                Browse files
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                hidden
                multiple
                accept={accept}
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }}
              />
            </Box>

            {/* Format / size info row — vertically centered in the space between the dropzone and How it works */}
            {stagedFiles.length === 0 && !uploading && (
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                  gap: 3,
                  px: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "10px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(39,109,93,0.18)" : "rgba(39,109,93,0.09)",
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 22, color: "#276d5d" }} />
                  </Box>
                  <Box sx={{ minWidth: 0, textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>Accepted formats</Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }} noWrap>Images, Videos, Documents, Audio</Typography>
                  </Box>
                </Box>

                <Box sx={{ width: "1px", backgroundColor: (t) => t.palette.divider }} />

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "10px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(39,109,93,0.18)" : "rgba(39,109,93,0.09)",
                    }}
                  >
                    <InsertDriveFileIcon sx={{ fontSize: 22, color: "#276d5d" }} />
                  </Box>
                  <Box sx={{ minWidth: 0, textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>Max file size</Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }} noWrap>5 MB per file</Typography>
                  </Box>
                </Box>
              </Box>
              </Box>
            )}

            {/* Staged files list */}
            {stagedFiles.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  mt: 2,
                  pr: 1.5,
                  mr: -1.5,
                  scrollbarWidth: "thin",
                  scrollbarColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(255,255,255,0.18) transparent" : "rgba(15,23,42,0.15) transparent",
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: 8,
                    backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.15)",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "rgba(15,23,42,0.25)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    {stagedFiles.length} file{stagedFiles.length > 1 ? "s" : ""} ready to upload
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                    Total size: {formatBytes(stagedFiles.reduce((sum, f) => sum + f.size, 0))}
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {stagedFiles.map((file, i) => {
                    const kind = classifyInboxMediaFile(file, platform, isTeamMemberChat).kind || "document";
                    const ext = file.name.includes(".") ? file.name.split(".").pop().toUpperCase() : "";
                    return (
                      <Box
                        key={i}
                        sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.75, borderRadius: "12px", border: (t) => `1px solid ${t.palette.divider}` }}
                      >
                        <Box
                          sx={{ position: "relative", flexShrink: 0, cursor: "pointer" }}
                          onClick={() => setPreviewStagedFile({ file, kind, ext })}
                        >
                          {kind === "image" ? (
                            <Box component="img" src={URL.createObjectURL(file)} alt={file.name} sx={{ width: 72, height: 72, borderRadius: "8px", objectFit: "cover", display: "block" }} />
                          ) : (
                            <Box sx={{ width: 72, height: 72, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "action.hover" }}>
                              <FileTypeIcon mediaType={kind} sx={{ fontSize: 36, color: "text.secondary" }} />
                            </Box>
                          )}
                          <CheckCircleIcon
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              fontSize: 20,
                              color: "#22C55E",
                              bgcolor: (t) => t.palette.mode === "dark" ? "#242424" : "#fff",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: "0.9rem", fontWeight: 600 }}>{file.name}</Typography>
                          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>{formatBytes(file.size)}{ext ? ` · ${ext}` : ""}</Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.75,
                              px: 1.1,
                              py: 0.35,
                              borderRadius: "999px",
                              backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(34,197,94,0.16)" : "rgba(34,197,94,0.12)",
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 15, color: "#16a34a" }} />
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#16a34a" }}>Ready</Typography>
                          </Box>
                        </Box>
                        <IconButton disabled={uploading} onClick={() => setStagedFiles((p) => p.filter((_, idx) => idx !== i))}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* How it works — only shown before any file is staged */}
            {stagedFiles.length === 0 && !uploading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 1,
                mt: "auto",
                flexShrink: 0,
                p: 2,
                borderRadius: "10px",
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#F3F5F7",
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#3b82f6",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
              <Box sx={{ maxWidth: 460 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem" }}>How it works</Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                  Files will be saved to your gallery. Select them from Your Gallery to send.
                </Typography>
              </Box>
            </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}`, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          variant="outlined"
          sx={{
            textTransform: "none",
            color: "text.secondary",
            borderRadius: "8px",
            fontSize: "0.8125rem",
            px: 2.75,
            py: 1,
            borderColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.16)" : "#E5E7EB",
            "&:hover": {
              borderColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.24)" : "#D1D5DB",
              backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F3F4F6",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            tab === 0
              ? !selectedItems.length || inserting
              : stagedFiles.length === 0 || uploading
          }
          onClick={tab === 0 ? handleInsert : handleUploadToGallery}
          startIcon={tab === 0 && !inserting ? <SendIcon sx={{ fontSize: 18 }} /> : null}
          sx={{
            textTransform: "none",
            backgroundColor: "#276d5d",
            borderRadius: "8px",
            fontSize: "0.8125rem",
            minWidth: 150,
            px: 3,
            py: 1,
            "&:hover": { backgroundColor: "#1f5447" },
            "&.Mui-disabled": { backgroundColor: "action.disabledBackground" },
          }}
        >
          {tab === 0 ? (
            inserting ? <CircularProgress size={16} sx={{ color: "white" }} /> : `Send${selectedItems.length ? ` ${selectedItems.length} file${selectedItems.length > 1 ? "s" : ""}` : ""}`
          ) : (
            uploading ? <CircularProgress size={16} sx={{ color: "white" }} /> : `Upload${stagedFiles.length ? ` ${stagedFiles.length} file${stagedFiles.length > 1 ? "s" : ""}` : ""} to Gallery`
          )}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete confirmation */}
    <Dialog open={Boolean(deleteConfirmItem)} onClose={handleCancelDeleteItem} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>Delete file</Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: "0.9rem", textAlign: "center" }}>
          Are you sure you want to permanently delete {deleteConfirmName}? This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleCancelDeleteItem}
          color="secondary"
          variant="outlined"
          disabled={deleteInProgress}
          sx={{
            textTransform: "none",
            fontSize: "0.8125rem",
            px: 2.75,
            py: 1,
            borderRadius: "8px",
            "&:hover": { backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F3F4F6" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDeleteItem}
          variant="contained"
          color="error"
          disabled={deleteInProgress}
          sx={{ textTransform: "none", fontSize: "0.8125rem", px: 2.75, py: 1, borderRadius: "8px" }}
        >
          {deleteInProgress ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Staged file preview */}
    <Dialog open={Boolean(previewStagedFile)} onClose={() => setPreviewStagedFile(null)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Typography noWrap sx={{ fontSize: "1rem", fontWeight: 700, pr: 2 }}>
          {previewStagedFile?.file?.name}
        </Typography>
        <IconButton size="small" onClick={() => setPreviewStagedFile(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          pb: 3,
        }}
      >
        {previewStagedFile && (() => {
          const url = URL.createObjectURL(previewStagedFile.file);
          const isPdf = previewStagedFile.ext === "PDF" || previewStagedFile.file.type === "application/pdf";
          if (previewStagedFile.kind === "image") {
            return <Box component="img" src={url} alt={previewStagedFile.file.name} sx={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: "8px" }} />;
          }
          if (previewStagedFile.kind === "video") {
            return <Box component="video" src={url} controls autoPlay sx={{ maxWidth: "100%", maxHeight: "75vh", borderRadius: "8px" }} />;
          }
          if (previewStagedFile.kind === "audio") {
            return <Box component="audio" src={url} controls sx={{ width: "100%" }} />;
          }
          if (isPdf) {
            return <Box component="iframe" src={url} title={previewStagedFile.file.name} sx={{ width: "100%", height: "75vh", border: "none" }} />;
          }
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 4 }}>
              <FileTypeIcon mediaType="document" sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", textAlign: "center" }}>
                Preview isn't available for this file type.
              </Typography>
            </Box>
          );
        })()}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ClusterDenMediaDialog;
